import { newTag, newPageTracker, newConsoleLoggerPlugin, newGovoltePlugin } from '@npotag/tag';
import type { NPOTag } from '@npotag/tag';
import type { InitialisationProps } from '@npotag/tag/dist/types/src/npoTag';

interface IData {
  prefix: string;
  chapter1?: string | null;
  chapter2?: string | null;
  slug?: string;
  searchPageID?: string | null;
  page?: string;
}
/**
 * Sends analytics data to NPO Tag - GoVolte for page display events.
 *
 * This composable tracks page views and sends relevant data to the NPO Tag system.
 *
 * @param prefix - A string representing the prefix for the page (e.g., a category or section identifier).
 * @param chapter1 - An optional page identifier for the page (optional), otherwise the prefix is used.
 * @param chapter2 - An optional chapter2 for the page (optional)
 * @param slug - An optional slug for the page (used to generate the article ID).
 * @param searchPageID - Optional search page ID for the page (used to generate the article ID).
 * @param page - Optional explicit page name (defaults to the slug, otherwise the prefix).
 *
 * @example
 * sendNPOTagAnalytics('news', 'breaking-news-12345');
 *
 * @returns {void} Sends the analytics event and logs a message if used outside a browser environment.
 */
export default function sendNPOTagAnalytics({
  slug,
  prefix,
  chapter1 = null,
  chapter2 = null,
  searchPageID = null,
  page,
}: IData): void {
  // Ensure function runs in a browser environment
  if (typeof window === 'undefined' || !import.meta.client) {
    console.warn('This function can only be used in a client-side browser environment.');
    return;
  }

  /**
   * Helper function to extract the article ID from the slug.
   */
  const getArticleID = (): string | undefined => {
    if (!slug) {
      return undefined;
    }
    const id = slug.split('-').at(-1);
    return id ? `${prefix}-${id}` : undefined;
  };

  // Access configuration for NPO Tag
  const { npoTagConfig } = useRuntimeConfig().public;
  const { debug, pageDisplay, configurations } = npoTagConfig;

  // Retrieve the article ID from the slug or searchPageID
  const articleID = searchPageID || getArticleID();

  // Construct final analytics data, with page display properties
  const finalDisplay = {
    ...pageDisplay,
    ...(articleID && { custom_label3: articleID }),
    page: page || slug || prefix,
    chapter_1: chapter1 || prefix,
    ...(chapter2 && { chapter_2: chapter2 }),
  };

  const npoTagInstance: NPOTag | undefined = newTag(
    configurations as InitialisationProps, // Set properties shared by all events
    // Create the tag plugins you want to use
    [
      ...(debug ? [newConsoleLoggerPlugin()] : []), // Add console logger plugin if debug is enabled
      newGovoltePlugin(),
    ],
  );

  if (!npoTagInstance) {
    console.error('Failed to create NPOTag instance');
    return;
  }

  // Initialize the NPO Tag with the final configurations
  const tracker = newPageTracker(npoTagInstance, finalDisplay);

  // Send a page view event
  tracker.pageView();
}
